/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable no-bitwise */
import { Injectable, inject } from '@angular/core';
import { ProductCategoryValuesModel } from '@warema/shared/company-search/models';
import {
    AppointmentRequestObjectType,
    AppointmentRequestProjectType,
    AppointmentRequestType,
    ConsultingType,
    ConcernType,
    AppointmentPostConsultationHome,
    AppointmentPostConsultationShowroom,
    AppointmentPostConsultationRepairService,
    CompanySearchResultSortCriteria,
    WorkflowOrigin,
    AppointmentRequestActivityType,
} from '@warema/shared/models';
import { GoogleTagManagerEventObject, GoogleTrackingService } from '../google-tracking.service';

@Injectable({
    providedIn: 'root',
})
export class AppointmentTrackingService {
    private readonly _googleTrackingService = inject(GoogleTrackingService);

    public trackAppointmentMenuButton(): void {
        this._googleTrackingService.track({ event: 'buttonClick', buttonClick: 'Termine' });
    }

    public trackFindMe(
        searchValue: string,
        origin: WorkflowOrigin,
        consultationId: string | undefined = undefined,
    ): void {
        const jsonObject = {
            event: 'appointmentConfigurator',
            appointmentConfigurator: 'Suche nach Ort',
            search: searchValue,
        };

        this._addOrigin(jsonObject, origin);
        this._addConsultationId(jsonObject, consultationId);

        this._googleTrackingService.track(jsonObject);
    }

    public trackDealerMenuButton(): void {
        this._googleTrackingService.track({ event: 'buttonClick', buttonClick: 'Händler' });
    }

    public trackDealerBannerButton(): void {
        this._googleTrackingService.track({
            event: 'bannerClick',
            bannerClick: 'Fachhändlersuche',
            buttonPosition: 'bannerClick',
        });
    }

    public trackAppointmentBannerButton(): void {
        this._googleTrackingService.track({ event: 'bannerClick', bannerClick: 'Termin vorschlagen' });
    }

    public trackLocationSearch(
        location: string,
        origin: WorkflowOrigin,
        consultationId: string | undefined = undefined,
    ): void {
        const jsonObject = {
            event: 'appointmentConfigurator',
            appointmentConfigurator: 'Suche nach Ort',
            search: location,
        };

        this._addOrigin(jsonObject, origin);
        this._addConsultationId(jsonObject, consultationId);

        this._googleTrackingService.track(jsonObject);
    }

    public trackAppointmentType(appointmentType: AppointmentRequestActivityType, origin: WorkflowOrigin): void {
        const jsonObject = {
            event: 'appointmentConfigurator',
            appointmentConfigurator: 'Terminart',
            appointmentType: this._getAppointmentTypeValue(appointmentType),
        };

        this._addOrigin(jsonObject, origin);

        this._googleTrackingService.track(jsonObject);
    }

    public trackAppointmentProducts(products: Array<string>): void {
        this._googleTrackingService.track({
            event: 'appointmentConfigurator',
            appointmentConfigurator: 'Produktwahl',
            products: products.toString(),
        });
    }

    public trackAppointmentCreation(dealerName: string): void {
        this._googleTrackingService.track({
            event: 'dealerSearchAppointmentRequest',
            option: 'Terminwunsch eingeben',
            partnerName: dealerName,
        });
    }

    public trackInstantOfferToggle(isToggled: boolean): void {
        this._googleTrackingService.track({
            event: 'toggleSwitch',
            toggleSwitch: 'Sofortangebot',
            status: isToggled ? 'on' : 'off',
        });
    }

    public trackFilter(sortCriteria: CompanySearchResultSortCriteria): void {
        this._googleTrackingService.track({
            event: 'filterUse',
            filterUse: 'Ergebnisliste',
            option: this._getSortCriteria(sortCriteria),
        });
    }

    public trackClickOfferRequest(
        dealerName: string,
        dealerAddressId: string,
        productSubcategories: string | null,
    ): void {
        const jsonObject = {
            event: 'itemClick',
            itemClick: 'Digitale Kaufberatung',
            partnerName: dealerName,
            partnerAddressId: dealerAddressId,
        };

        this._addBusinessUnit(jsonObject, productSubcategories);
        this._googleTrackingService.track(jsonObject);
    }

    public trackClickRequestInstantOffer(productName: string, email: string): void {
        this._googleTrackingService.track({
            event: 'itemClick',
            itemClick: 'Digitale Kaufberatung',
            productName,
            origin: 'Projekt',
            email,
            businessUnit: 'Outdoor Living',
        });
    }

    public trackClickRequestConsultationAppointment(productName: string, email: string): void {
        this._googleTrackingService.track({
            event: 'itemClick',
            itemClick: 'Beratungstermin anfragen',
            productName,
            origin: 'Projekt',
            email,
            businessUnit: 'Outdoor Living',
        });
    }

    public trackClickDownloadOffer(fileExtension: string, fileName: string): void {
        this._googleTrackingService.track({
            event: 'fileDownload',
            itemClick: 'Digitale Kaufberatung',
            fileExtension,
            fileName,
            origin: 'Projekt',
            businessUnit: 'Outdoor Living',
        });
    }

    public trackSubmitInstantOffer(dealerName: string, dealerAddressId: string, email: string): void {
        this._googleTrackingService.track({
            event: 'formSubmit',
            formtype: 'Angebot abschicken',
            partnerName: dealerName,
            partnerAddressId: dealerAddressId,
            email,
            origin: 'Projekt',
            businessUnit: 'Outdoor Living',
        });
    }

    public trackRequestInstantOfferFromProjects(projectName: string, projectId: string): void {
        this._googleTrackingService.track({
            event: 'itemClick',
            itemClick: 'Angebot anfragen',
            projectName,
            projectId,
        });
    }

    public trackCreateAppointmentFromProjects(projectName: string, projectId: string): void {
        this._googleTrackingService.track({
            event: 'itemClick',
            itemClick: 'Beratungstermin anfragen',
            projectName,
            projectId,
        });
    }

    public trackCreateAppointmentFromProjectsWithDetails(
        projectName: string,
        projectId: string,
        productName: string,
        email: string,
    ): void {
        this._googleTrackingService.track({
            event: 'itemClick',
            itemClick: 'Beratungstermin anfragen',
            projectName,
            projectId,
            productName,
            origin: 'Projekt',
            email,
            businessUnit: 'Outdoor Living',
        });
    }

    public trackDealerInformation(dealerName: string, dealerAddressId: string): void {
        this._googleTrackingService.track({
            event: 'dealerSearchMoreInformation',
            option: 'Weitere Informationen',
            partnerName: dealerName,
            partnerAddressId: dealerAddressId,
        });
    }

    public trackSubmitServiceAppointmentV2(
        details: AppointmentPostConsultationRepairService,
        goalValue: number,
        requestID: string,
        consultationId: string | undefined = undefined,
        productSubcategories: string | null = null,
    ): void {
        const jsonObject = {
            event: 'formSubmit',
            formtype: 'Terminwunsch Service',
            requestType: this._getRequestTypeValue(details.requestType),
            matter: this._getConcernType(details.concernType),
            date: details.installationDate,
            storey: `Stockwerk: ${details.floorNumber}`,
            installation: this._getInstallation(
                details.isExternallyAccessible,
                details.isLadderRequired,
                details.isRoof,
            ),
            evaluation: details.isLackAlreadyAssessed ? 'Ja' : 'Nein',
            requestID,
            goalValue,
            email: details.userAddress.email,
        };

        this._addConsultationId(jsonObject, consultationId);
        this._addBusinessUnit(jsonObject, productSubcategories);
        this._googleTrackingService.track(jsonObject);
    }

    public trackSubmitHomeAppointmentV2(
        details: AppointmentPostConsultationHome,
        goalValue: number,
        requestID: string,
        origin: WorkflowOrigin,
        consultationId: string | undefined,
        productSubcategories: string | null,
    ): void {
        const jsonObject = {
            event: 'formSubmit',
            formtype: 'Terminwunsch Beratung zu Hause',
            requestType: this._getRequestTypeValue(details.requestType),
            consultancy: this._getConsultancy(details.consultingType),
            project: this._getProjectTypeValue(details.projectType),
            windowNumber: details.windowsCount,
            requestID,
            goalValue,
            email: details.userAddress.email,
        };

        this._addOrigin(jsonObject, origin);
        this._addConsultationId(jsonObject, consultationId);
        this._addBusinessUnit(jsonObject, productSubcategories);
        this._googleTrackingService.track(jsonObject);
    }

    public trackSubmitShowroomAppointmentV2(
        details: AppointmentPostConsultationShowroom,
        goalValue: number,
        requestID: string,
        origin: WorkflowOrigin,
        consultationId: string | undefined,
        productSubcategories: string | null,
    ): void {
        const jsonObject = {
            event: 'formSubmit',
            formtype: 'Terminwunsch Ausstellungsraum',
            requestType: this._getRequestTypeValue(details.requestType),
            housing: this._getHousingValue(details.objectType),
            project: this._getProjectTypeValue(details.projectType),
            windowNumber: details.windowsCount,
            requestID,
            goalValue,
            email: details.userAddress.email,
        };
        this._addOrigin(jsonObject, origin);
        this._addConsultationId(jsonObject, consultationId);
        this._addBusinessUnit(jsonObject, productSubcategories);
        this._googleTrackingService.track(jsonObject);
    }

    public trackProductDetail(productDetail: ProductCategoryValuesModel, requestID: string): void {
        this._googleTrackingService.track({
            event: 'productDetail',
            requestID,
            productName: productDetail.productCategory,
            productValue: productDetail.value,
            businessUnit: productDetail.businessUnit,
        });
    }

    public trackDigitalBuyingAdvisorSelection(
        partnerName: string,
        partnerAddressId: string,
        businessUnit: string,
    ): void {
        this._googleTrackingService.track({
            event: 'itemClick',
            itemClick: 'Digitale Kaufberatung',
            partnerName,
            partnerAddressId,
            businessUnit,
        });
    }

    private _getProjectTypeValue(projectType: AppointmentRequestProjectType): string | undefined {
        switch (projectType) {
            case AppointmentRequestProjectType.NewBuilding:
                return 'Neubau';
            case AppointmentRequestProjectType.Renovation:
                return 'Renovierung';
        }
        return undefined;
    }

    private _getHousingValue(housingType: AppointmentRequestObjectType): string | undefined {
        switch (housingType) {
            case AppointmentRequestObjectType.House:
                return 'Haus';
            case AppointmentRequestObjectType.ApartmentOwner:
                return 'Eigentumswohnung';
            case AppointmentRequestObjectType.ApartmentRent:
                return 'Mietwohnung';
        }
        return undefined;
    }

    private _getRequestTypeValue(requestType: AppointmentRequestType): string | undefined {
        switch (requestType) {
            case AppointmentRequestType.Private:
                return 'privat';
            case AppointmentRequestType.Commercial:
                return 'gewerblich';
        }
        return undefined;
    }

    private _getAppointmentTypeValue(appointmentType: AppointmentRequestActivityType): string | undefined {
        switch (appointmentType) {
            case AppointmentRequestActivityType.ConsultationAtHome:
                return 'Beratung zu Hause';
            case AppointmentRequestActivityType.ConsultationAtShowroom:
                return 'Beratung im Ausstellungsraum';
            case AppointmentRequestActivityType.RepairOrService:
                return 'Service-Termin';
            case AppointmentRequestActivityType.Quotation:
                return 'Angebot anfordern';
        }
        return undefined;
    }

    private _addOrigin(jsonObject: GoogleTagManagerEventObject, origin: WorkflowOrigin): void {
        if (origin === WorkflowOrigin.WaremaPlusProjects || origin === WorkflowOrigin.Configurator) {
            jsonObject['origin'] = 'Projekt';
        }
    }

    private _addConsultationId(jsonObject: GoogleTagManagerEventObject, consultationId: string | undefined): void {
        if (consultationId !== undefined) {
            jsonObject['consultationId'] = consultationId;
        }
    }

    private _getInstallation(isExternallyAccessible: boolean, isLadderRequired: boolean, isRoof: boolean): string {
        const result: Array<string> = [];
        if (isExternallyAccessible) {
            result.push('Nur von außen zugänglich');
        }
        if (isLadderRequired) {
            result.push('Leiter erforderlich');
        }
        if (isRoof) {
            result.push('Dach');
        }
        return result.toString();
    }

    private _getConcernType(concernType: ConcernType): string {
        switch (concernType) {
            case ConcernType.Service:
                return 'Service';
            case ConcernType.Repair:
                return 'Reperatur';
            default:
                return 'Keine Auswahl';
        }
    }

    private _getSortCriteria(sortCriteria: CompanySearchResultSortCriteria): string | undefined {
        switch (sortCriteria) {
            case CompanySearchResultSortCriteria.Distance:
                return 'Entfernung';
            case CompanySearchResultSortCriteria.GoogleRating:
                return 'Google Rating';
            case CompanySearchResultSortCriteria.PrimeStatus:
                return 'Prime Status';
        }
        return undefined;
    }

    private _getConsultancy(consultancyTypes: number): string {
        const result: Array<string> = [];

        if (consultancyTypes & ConsultingType.Measurement) {
            result.push('Aufmaß');
        }
        if (consultancyTypes & ConsultingType.Product) {
            result.push('Produkt');
        }
        if (consultancyTypes & ConsultingType.PriceInformation) {
            result.push('Preisinformation');
        }
        if (consultancyTypes & ConsultingType.DesignOrColor) {
            result.push('Design oder Farbe');
        }
        if (consultancyTypes & ConsultingType.Technical) {
            result.push('Technisch');
        }

        return result.toString();
    }

    private _addBusinessUnit(jsonObject: GoogleTagManagerEventObject, productSubcategories: string | null): void {
        const categories = productSubcategories?.split(',') ?? [];

        const hasPatio = categories.some(x => x.startsWith('3'));
        const hasWindow = categories.some(x => x.startsWith('1'));

        if (hasPatio && hasWindow) {
            jsonObject['businessUnit'] = 'Outdoor Living, Window Systems';
        } else if (hasPatio) {
            jsonObject['businessUnit'] = 'Outdoor Living';
        } else if (hasWindow) {
            jsonObject['businessUnit'] = 'Window Systems';
        }
    }
}
